<template>
	<div class="customers-page">
		<div class="customers-layout">
			<!-- Left Panel: Customer List -->
			<div class="customers-list-panel">
				<div class="list-header">
					<h2>Customers</h2>
				</div>
				<div class="search-box">
					<input
						v-model="searchQuery"
						type="text"
						placeholder="Search customers..."
						class="search-input"
					/>
				</div>
				<div class="customers-list">
					<div
						v-if="filteredCustomers.length > 0"
						class="customer-items"
					>
						<div
							v-for="customer in filteredCustomers"
							:key="customer.id"
							class="customer-item"
							:class="{ active: selectedCustomer && selectedCustomer.id === customer.id }"
							@click="selectCustomer(customer)"
						>
							<div class="customer-name">
								{{ customer.person.firstName }} {{ customer.person.lastName }}
							</div>
							<div class="customer-linked" :class="{ linked: customer.employerID }">
								{{ customer.employerID ? 'Linked' : 'Not Linked' }}
							</div>
						</div>
					</div>
					<div v-else class="empty-state">
						No customers found
					</div>
				</div>
			</div>

			<!-- Right Panel: Customer Details -->
			<div class="customer-details-panel">
				<div v-if="selectedCustomer" class="details-content">
					<!-- Customer ID -->
					<div class="detail-header">
						{{ selectedCustomer.id }}
					</div>

					<!-- Personal Info Row 1 -->
					<div class="form-row">
						<div class="form-group">
							<label>Title:</label>
							<input
								:value="selectedCustomer.person.title"
								type="text"
								class="form-input"
								readonly
							/>
						</div>
						<div class="form-group">
							<label>First Name:</label>
							<input
								:value="selectedCustomer.person.firstName"
								type="text"
								class="form-input"
								readonly
							/>
						</div>
						<div class="form-group">
							<label>Last Name:</label>
							<input
								:value="selectedCustomer.person.lastName"
								type="text"
								class="form-input"
								readonly
							/>
						</div>
					</div>

					<!-- Personal Info Row 2 -->
					<div class="form-row">
						<div class="form-group">
							<label>Email:</label>
							<input
								:value="selectedCustomer.person.email"
								type="text"
								class="form-input"
								readonly
							/>
						</div>
						<div class="form-group">
							<label>Mobile:</label>
							<input
								:value="selectedCustomer.person.mobilePhone"
								type="text"
								class="form-input"
								readonly
							/>
						</div>
						<div class="form-group">
							<label>DOB:</label>
							<input
								:value="formatDate(selectedCustomer.person.dob)"
								type="text"
								class="form-input"
								readonly
							/>
						</div>
					</div>

					<!-- Generic Customer Note -->
					<div class="form-group customer-note-group">
						<div class="label-with-button">
							<label>Customer Note:</label>
							<button
								v-if="editingCustomerNote !== selectedCustomer.note"
								class="action-btn primary small"
								@click="saveCustomerNote"
								:disabled="noteUpdating"
							>
								{{ noteUpdating ? 'Saving...' : 'Save' }}
							</button>
						</div>
						<textarea
							v-model="editingCustomerNote"
							class="form-textarea"
							rows="3"
							placeholder="Add a general note about this customer..."
						></textarea>
					</div>

					<!-- Employer Section -->
					<div class="employer-section">
						<div class="section-header">
							<label>Employer:</label>
							<button class="action-btn secondary" @click="showLinkEmployerModal = true">
								Link Employer
							</button>
						</div>
						<div v-if="selectedCustomer.employer" class="employer-info">
							<div class="employer-name">{{ selectedCustomer.employer.name }}</div>
							<button class="action-btn danger small" @click="unlinkEmployer">
								Unlink
							</button>
						</div>
						<div v-else class="no-employer">
							No employer linked
						</div>
					</div>

					<!-- Dates Section -->
					<div class="dates-section">
						<div class="date-item">
							<label>Signed Up:</label>
							<div class="date-value">{{ formatDateTime(selectedCustomer.createDateTime) }}</div>
						</div>
						<div class="date-item">
							<label>Last Logged In:</label>
							<div class="date-value">{{ selectedCustomer.lastLoginDateTime ? formatDateTime(selectedCustomer.lastLoginDateTime) : 'Never' }}</div>
						</div>
					</div>

					<!-- Manage Credit Button -->
					<div class="actions-row">
						<button class="action-btn secondary" @click="openNotesDrawer">
							Customer Notes
						</button>
						<button class="action-btn secondary" @click="showManageCreditModal = true">
							Manage Credit
						</button>
					</div>

					<!-- DPA Consent Section -->
					<div class="dpa-section">
						<div class="dpa-warning">
							<input
								type="checkbox"
								class="form-checkbox"
								v-model="permissionGranted"								
							/>
							<span>I have obtained explicit permission from the customer to view their details.</span>
						</div>
						<p class="dpa-text">
							Section 170 of the Data Protection Act 2018 (DPA 2018) makes it a criminal offense to unlawfully obtain personal data.
						</p>
					</div>

					<!-- Detailed Data Sections (shown after permission granted) -->
					<div v-if="permissionGranted">
						<!-- Insurance/Policies Section -->
						<ExpandableDataTable
							v-if="policies && policies.length > 0"
							:title="`Insurance`"
							:items="policies"
							:columns="policyColumns"
							:expanded-index="expandedPolicy"
							@toggle-details="togglePolicyDetails"
						>
							<template #details="{ item: policy }">
								<div class="form-row">
									<div class="form-group">
										<label>Policy Number:</label>
										<input v-model="policy.policyNumber" class="form-input" readonly />
									</div>
									<div class="form-group">
										<label>Type:</label>
										<input v-model="policy.policyType.name" class="form-input" readonly />
									</div>
									<div class="form-group">
										<label>Provider:</label>
										<input v-model="policy.provider.name" class="form-input" readonly />
									</div>
								</div>
								<div class="form-row">
									<div class="form-group">
										<label>Reg/Address:</label>
										<input :value="policy.insuredItems?.[0]?.vehicleRegistration || policy.insuredItems?.[0]?.addressInsured || '-'" class="form-input" readonly />
									</div>
									<div class="form-group">
										<label>Premium (£):</label>
										<input v-model="policy.annualPremium" type="number" class="form-input" />
									</div>
									<div class="form-group">
										<label>Excess (£):</label>
										<input v-model="policy.excess" type="number" class="form-input" />
									</div>
								</div>
								<div class="form-row">
									<div class="form-group">
										<label>No Claims Years:</label>
										<input v-model="policy.noClaimsYears" type="number" class="form-input" placeholder="Enter No Claims Years" />
									</div>
									<div class="form-group">
										<label>Start Date:</label>
										<input v-model="policy.startDateString" class="form-input" />
									</div>
									<div class="form-group">
										<label>End Date:</label>
										<input v-model="policy.endDateString" class="form-input" />
									</div>
								</div>
								<div class="form-row">
									<div class="form-group full-width">
										<label>AIOP Object:</label>
										<code class="aiop-code">{{ JSON.stringify(policy, null, 2) }}</code>
									</div>
								</div>
							</template>
						</ExpandableDataTable>

						<!-- Investments Section -->
						<ExpandableDataTable
							v-if="investments && investments.length > 0"
							:title="`Investments`"
							:items="investments"
							:columns="investmentColumns"
							:expanded-index="expandedInvestment"
							@toggle-details="toggleInvestmentDetails"
						>
							<template #details="{ item: investment }">
								<div class="form-row">
									<div class="form-group">
										<label>Account Number:</label>
										<input v-model="investment.accountNumber" class="form-input" readonly />
									</div>
									<div class="form-group">
										<label>Account Type:</label>
										<input v-model="investment.accountType.name" class="form-input" readonly />
									</div>
									<div class="form-group">
										<label>Provider:</label>
										<input v-model="investment.provider.name" class="form-input" readonly />
									</div>
								</div>
								<div class="form-row">
									<div class="form-group">
										<label>Balance (£):</label>
										<input v-model="investment.balance" type="number" class="form-input" />
									</div>
									<div class="form-group">
										<label>Allowance Used (£):</label>
										<input v-model="investment.taxYearAllowanceUsed" type="number" class="form-input" />
									</div>
								</div>
								<div class="form-row">
									<div class="form-group">
										<label>Personal Contribution (£):</label>
										<input v-model="investment.personalMonthlyContribution" type="number" class="form-input" />
									</div>
									<div class="form-group">
										<label>Employer Contribution (£):</label>
										<input v-model="investment.employerMonthlyContribution" type="number" class="form-input" />
									</div>
									<div class="form-group">
										<label>Retirement Age:</label>
										<input v-model="investment.retirementAge" type="number" class="form-input" />
									</div>
								</div>
								<div class="form-row">
									<div class="form-group full-width">
										<label>AIOP Object:</label>
										<code class="aiop-code">{{ JSON.stringify(investment, null, 2) }}</code>
									</div>
								</div>
							</template>
						</ExpandableDataTable>

						<!-- Completed Moves Section -->
						<div v-if="completedMoves && completedMoves.length > 0" class="data-section">
							<h4>Completed Moves ({{ completedMoves.length }})</h4>
							<div class="table-wrapper">
								<table class="detail-table">
									<thead>
										<tr>
											<th>Completed Moves</th>
											<th>Description</th>
										</tr>
									</thead>
									<tbody>
										<tr v-for="(move, index) in completedMoves" :key="index">
											<td>{{ move.completedMoves || '-' }}</td>
											<td>{{ move.description || '-' }}</td>
										</tr>
									</tbody>
								</table>
							</div>
						</div>

						<!-- Pending Moves Section -->
						<div v-if="moves && moves.length > 0" class="data-section">
							<h4>Pending Moves ({{ moves.length }})</h4>
							<div class="table-wrapper">
								<table class="detail-table">
									<thead>
										<tr>
											<th>Pending Moves</th>
											<th>Description</th>
										</tr>
									</thead>
									<tbody>
										<tr v-for="(move, index) in moves" :key="index">
											<td>{{ move.pendingMoves || '-' }}</td>
											<td>{{ move.description || '-' }}</td>
										</tr>
									</tbody>
								</table>
							</div>
						</div>

						<!-- Fact Card Section -->
						<div v-if="selectedCustomer.facts" class="data-section">
							<h4>Fact Card</h4>
							<div class="fact-card-wrapper">
								<div class="fact-card">
									<div v-for="name in Object.keys(selectedCustomer.facts)" :key="name" class="fact-row">
										<label>{{ name }}:</label>
										<input v-model="selectedCustomer.facts[name]" class="form-input" />
									</div>
								</div>
							</div>
							<button class="action-btn secondary" @click="postFactCard" :disabled="generatingMoves">
								{{ generatingMoves ? 'Generating...' : 'Generate Moves' }}
							</button>
						</div>
					</div>
				</div>
				<div v-else class="empty-state">
					Select a customer to view details
				</div>
			</div>
		</div>

		<!-- Customer Notes Drawer -->
		<CustomerNotesDrawer
			:show="showNotesDrawer"
			:notes="customerNotes"
			:loading="notesLoading"
			:method-options="noteMethodOptions"
			:reason-options="noteReasonOptions"
			:customers="customers"
			@close="closeNotesDrawer"
			@add-note="handleAddNote"
			@error="showError"
		/>

		<!-- Link Employer Modal -->
		<MGDialog
			v-model="showLinkEmployerModal"
			title="Link Employer"
			max-width="500px"
			content-height="400px"
		>
			<template #content>
				<div class="search-box">
					<input
						v-model="employerSearchQuery"
						type="text"
						placeholder="Search employers..."
						class="search-input"
					/>
				</div>
				<div class="employers-list">
					<div v-if="filteredEmployers.length > 0">
						<div
							v-for="employer in filteredEmployers"
							:key="employer.id"
							class="employer-option"
							@click="confirmLinkEmployer(employer)"
						>
							{{ employer.name }}
						</div>
					</div>
					<div v-else class="empty-state">
						No employers found
					</div>
				</div>
			</template>
			<template #actions>
				<button class="action-btn secondary" @click="showLinkEmployerModal = false">Cancel</button>
			</template>
		</MGDialog>

		<!-- Manage Credit Modal -->
		<MGDialog
			v-model="showManageCreditModal"
			title="Manage Customer Credit"
			max-width="600px"
			content-height="400px"
		>
			<template #content>
				<!-- Add Credit Section -->
				<div class="credit-section">
					<h4>Add Credit</h4>
					<div class="form-group">
						<label>Amount (£):</label>
						<input
							v-model="creditAmount"
							type="number"
							placeholder="Enter amount"
							class="form-input"
							step="0.01"
						/>
					</div>
					<button class="action-btn primary" @click="addCredit">Add Credit</button>
				</div>

				<!-- Credit Transaction History Section -->
				<div class="credit-history">
					<h4>Credit Transaction History</h4>
					<div class="table-wrapper">
						<table class="detail-table" v-if="creditTransactions && creditTransactions.length > 0">
							<thead>
								<tr>
									<th>Date</th>
									<th>Amount (£)</th>
								</tr>
							</thead>
							<tbody>
								<tr v-for="transaction in creditTransactions" :key="transaction.id">
									<td>{{ formatDateTime(transaction.createdDateTime) }}</td>
									<td>{{ transaction.amount.toFixed(2) }}</td>
								</tr>
							</tbody>
						</table>
						<div v-else class="empty-state">
							No credit transactions yet
						</div>
					</div>
				</div>
			</template>
			<template #actions>
				<button class="action-btn secondary" @click="showManageCreditModal = false">Close</button>
			</template>
		</MGDialog>

		<!-- Success Toast Notification -->
		<v-snackbar
			v-model="showSuccessToast"
			:timeout="3000"
			location="bottom"
			color="success"
		>
			{{ successMessage }}
			<template #actions>
				<v-btn
					icon="mdi-close"
					size="small"
					@click="showSuccessToast = false"
				></v-btn>
			</template>
		</v-snackbar>

		<!-- Error Toast Notification -->
		<v-snackbar
			v-model="showErrorToast"
			:timeout="4000"
			location="bottom"
			color="error"
		>
			{{ errorMessage }}
			<template #actions>
				<v-btn
					icon="mdi-close"
					size="small"
					@click="showErrorToast = false"
				></v-btn>
			</template>
		</v-snackbar>
	</div>
</template>

<script>
import AdminServices from '@/admin/mixins/AdminServices';
import MGDialog from '@/components/elements/MGDialog.vue';
import ExpandableDataTable from '@/admin/components/ExpandableDataTable.vue';
import CustomerNotesDrawer from '@/admin/components/CustomerNotesDrawer.vue';

export default {
	name: 'CustomersPage',
	mixins: [AdminServices],
	components: {
		MGDialog,
		ExpandableDataTable,
		CustomerNotesDrawer
	},
	data() {
		return {
			customers: [],
			selectedCustomer: null,
			searchQuery: '',
			customersLoading: false,
			customersError: null,
			showLinkEmployerModal: false,
			employerSearchQuery: '',
			availableEmployers: [],
			employers: [],
			showSuccessToast: false,
			successMessage: '',
			showErrorToast: false,
			errorMessage: '',
			permissionGranted: false,
			policies: [],
			investments: [],
			moves: [],
			completedMoves: [],
			expandedPolicy: null,
			expandedInvestment: null,
			selfId: null,
			creditTransactions: [],
			showManageCreditModal: false,
			creditAmount: '',
			generatingMoves: false,
			policyColumns: [
				{ key: 'provider.name', label: 'Provider' },
				{ key: 'policyType.name', label: 'Type' },
				{ key: 'policyNumber', label: 'Policy Number' },
				{
					key: 'insuredItems',
					label: 'Reg',
					formatter: (policy) => {
						return policy.insuredItems?.[0]?.vehicleRegistration ||
							policy.insuredItems?.[0]?.addressInsured ||
							'-';
					}
				}
			],
			investmentColumns: [
				{ key: 'provider.name', label: 'Provider' },
				{ key: 'accountType.name', label: 'Type' },
				{ key: 'accountNumber', label: 'Account Number' },
				{
					key: 'balance',
					label: 'Balance',
					formatter: (investment) => {
						return investment.balance ? `£${investment.balance.toFixed(2)}` : '£0.00';
					}
				}
			],
			customerNotes: [],
			showNotesDrawer: false,
			notesLoading: false,
			noteMethodOptions: ['zendesk', 'phone', 'email', 'in-person', 'chat', 'other'],
			noteReasonOptions: ['inquiry', 'complaint', 'follow-up', 'support', 'other'],
			editingCustomerNote: '',
			noteUpdating: false
		};
	},
	computed: {
		filteredCustomers() {
			if (!this.searchQuery.trim()) {
				return this.customers;
			}
			const query = this.searchQuery.toLowerCase();
			return this.customers.filter(cust => {
				const fullName = `${cust.person.firstName} ${cust.person.lastName}`.toLowerCase();
				const email = cust.person.email.toLowerCase();
				return fullName.includes(query) || email.includes(query) || cust.id.toLowerCase().includes(query);
			});
		},
		filteredEmployers() {
			if (!this.employerSearchQuery.trim()) {
				return this.employers;
			}
			const query = this.employerSearchQuery.toLowerCase();
			return this.employers.filter(emp =>
				emp.name.toLowerCase().includes(query) ||
				emp.id.toLowerCase().includes(query)
			);
		}
	},
	methods: {
		showToast(message) {
			this.successMessage = message;
			this.showSuccessToast = true;
		},
		showError(message) {
			this.errorMessage = message;
			this.showErrorToast = true;
		},
		selectCustomer(customer) {
			// Reset permission and data when selecting new customer
			this.permissionGranted = false;
			this.creditAmount = '';
			this.editingCustomerNote = '';

			// Determine if we should fetch facts (only if it's the current user)
			const getFacts = this.selfId === customer.id;

			// Fetch detailed customer information from the API
			this._getCustomerDetails(customer.id, getFacts, (customerData) => {
				this.selectedCustomer = customerData;
				this.editingCustomerNote = customerData.note || '';

				// Pre-fetch all related data
				this._getCustomerPolicies(customer.id, (data) => {
					this.policies = data || [];
				});

				this._getCustomerInvestments(customer.id, (data) => {
					this.investments = data || [];
				});

				this._getCustomerMoves(customer.id, (data) => {
					this.moves = data || [];
				});

				this._getCustomerCompletedMoves(customer.id, (data) => {
					this.completedMoves = data || [];
				});

				// Fetch credit transactions
				this._getCustomerCreditTransactions(customer.id, (data) => {
					this.creditTransactions = data || [];
				});
			});
		},
		confirmLinkEmployer(employer) {
			this._linkEmployerToCustomer(this.selectedCustomer.id, employer.id, () => {
				this.showLinkEmployerModal = false;
				this.employerSearchQuery = '';
				// Update selected customer with employer info
				this.selectedCustomer.employerID = employer.id;
				this.selectedCustomer.employer = employer;
				// Refresh customers list
				this._getCustomers();
				this.showToast('Employer linked successfully');
			}, (error) => {
				this.showError(error);
			});
		},
		unlinkEmployer() {
			this._linkEmployerToCustomer(this.selectedCustomer.id, null, () => {
				// Update selected customer
				this.selectedCustomer.employerID = null;
				this.selectedCustomer.employer = null;
				// Refresh customers list
				this._getCustomers();
				this.showToast('Employer unlinked successfully');
			}, (error) => {
				this.showError(error);
			});
		},
		formatDate(dateString) {
			if (!dateString) return '';
			const date = new Date(dateString);
			return date.toLocaleDateString('en-GB', {
				year: 'numeric',
				month: '2-digit',
				day: '2-digit'
			});
		},
		formatDateTime(dateString) {
			if (!dateString) return '';
			const date = new Date(dateString);
			return date.toLocaleString('en-GB', {
				year: 'numeric',
				month: '2-digit',
				day: '2-digit',
				hour: '2-digit',
				minute: '2-digit'
			});
		},
		togglePolicyDetails(index) {
			this.expandedPolicy = this.expandedPolicy === index ? null : index;
		},
		toggleInvestmentDetails(index) {
			this.expandedInvestment = this.expandedInvestment === index ? null : index;
		},
		postFactCard() {
			if (!this.selectedCustomer || !this.selectedCustomer.facts) {
				this.showError('No facts to post');
				return;
			}

			this.generatingMoves = true;

			// Step 1: Post facts for the selected customer
			this._postFactCard(this.selectedCustomer.id, this.selectedCustomer.facts, () => {
				// Step 2: Generate moves for the selected customer
				this._generateMoves(this.selectedCustomer.id, () => {
					// Step 3: Refresh selected customer to show updated facts
					this._getCustomerDetails(this.selectedCustomer.id, true, (customerData) => {
						this.selectedCustomer = customerData;

						// Step 4: Refresh moves for the selected customer
						this._getCustomerMoves(this.selectedCustomer.id, (data) => {
							this.moves = data || [];
							this.showToast('Moves generated successfully');
							this.generatingMoves = false;
						});
					});
				}, (error) => {
					this.showError(error);
					this.generatingMoves = false;
				});
			}, (error) => {
				this.showError(error);
				this.generatingMoves = false;
			});
		},
		addCredit() {
			if (!this.creditAmount || parseFloat(this.creditAmount) <= 0) {
				this.showError('Please enter a valid amount');
				return;
			}
			if (!this.selectedCustomer || !this.selectedCustomer.id) {
				this.showError('No customer selected');
				return;
			}
			const amount = parseFloat(this.creditAmount);
			this._addCustomerCredit(this.selectedCustomer.id, amount, () => {
				// Refresh credit transactions after successful add
				this._getCustomerCreditTransactions(this.selectedCustomer.id, (data) => {
					this.creditTransactions = data || [];
				});
				this.showToast('Credit added successfully');
				this.creditAmount = '';
			}, (error) => {
				this.showError(error);
			});
		},
		openNotesDrawer() {
			this.showNotesDrawer = true;
			this.notesLoading = true;
			this._getCustomerNotes(this.selectedCustomer.id, (notes) => {
				this.customerNotes = notes || [];
				this.notesLoading = false;
			});
		},
		closeNotesDrawer() {
			this.showNotesDrawer = false;
		},
		handleAddNote(noteData) {
			noteData.customerID = this.selectedCustomer.id;
			noteData.addedByCustomerID = this.selfId;
			// This method is called from the CustomerNotesDrawer component
			// noteData already has validation done in the component
			this._createCustomerNote(this.selectedCustomer.id, noteData, () => {
				this.showToast('Note added successfully');
				// Refresh notes list
				this._getCustomerNotes(this.selectedCustomer.id, (notes) => {
					this.customerNotes = notes || [];
				});
			}, (error) => {
				this.showError(error);
			});
		},
		saveCustomerNote() {
			this.noteUpdating = true;
			const updateData = {
				note: this.editingCustomerNote
			};

			this._updateCustomerNote(this.selectedCustomer.id, updateData, () => {
				// Update the selected customer with the new note
				this.selectedCustomer.note = this.editingCustomerNote;
				this.showToast('Customer note saved successfully');
				this.noteUpdating = false;
			}, (error) => {
				this.showError(error);
				this.noteUpdating = false;
			});
		}
	},
	mounted() {
		console.log('Customers Page loaded');
		// Fetch current user's ID
		this.getSelf();
		this._getCustomers();
		this._getEmployers();
	}
};
</script>

<style scoped>
.customers-page {
	min-height: 100%;
	padding: 20px;
}

.customers-layout {
	display: grid;
	grid-template-columns: 300px 1fr;
	gap: 20px;
	height: 100%;
}

/* Left Panel: Customer List */
.customers-list-panel {
	background-color: var(--admin-bg-secondary);
	border-radius: var(--border-radius-m);
	padding: 20px;
	display: flex;
	flex-direction: column;
	box-shadow: var(--box-shadow);
}

.list-header {
	margin-bottom: 15px;
}

.list-header h2 {
	margin: 0;
	font-size: 16px;
	color: var(--admin-text-primary);
}

.search-box {
	margin-bottom: 20px;
}

.search-input {
	width: 100%;
	padding: 10px;
	border: 1px solid var(--admin-border);
	border-radius: var(--border-radius-s);
	background-color: var(--admin-bg-secondary);
	color: var(--admin-text-primary);
	font-size: 13px;
	transition: all 0.2s ease;
}

.search-input:focus {
	outline: none;
	border-color: var(--background-color-primary);
	box-shadow: 0 0 0 2px rgba(51, 40, 71, 0.1);
}

.customers-list {
	flex: 1;
	overflow-y: auto;
}

.customer-items {
	display: flex;
	flex-direction: column;
	gap: 2px;
}

.customer-item {
	padding: 12px;
	background-color: var(--admin-bg-secondary);
	color: var(--admin-text-primary);
	cursor: pointer;
	border-radius: var(--border-radius-s);
	font-size: 13px;
	transition: all 0.2s ease;
	border-left: 3px solid transparent;
	display: flex;
	justify-content: space-between;
	align-items: center;
	gap: 10px;
}

.customer-item:hover {
	background-color: var(--admin-hover-bg);
}

.customer-item.active {
	background-color: #666 !important;
	border-left-color: var(--background-color-primary) !important;
	font-weight: 500 !important;
	color: white !important;
}

.customer-name {
	flex: 1;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.customer-linked {
	font-size: 11px;
	padding: 4px 8px;
	border-radius: 3px;
	background-color: var(--admin-hover-bg);
	color: var(--admin-text-secondary);
	white-space: nowrap;
}

.customer-linked.linked {
	background-color: #4caf50;
	color: white;
	font-weight: 500;
}

.empty-state {
	padding: 20px;
	text-align: center;
	color: var(--admin-text-secondary);
	font-size: 13px;
}

/* Right Panel: Customer Details */
.customer-details-panel {
	background-color: var(--admin-bg-secondary);
	border-radius: var(--border-radius-m);
	padding: 30px;
	overflow-y: auto;
	box-shadow: var(--box-shadow);
}

.details-content {
	max-width: 900px;
}

/* Employer & DPA Sections kept - see below */

/* Employer Section */
.employer-section {
	background-color: var(--admin-bg-secondary);
	padding: 15px;
	border-radius: var(--border-radius-s);
	margin-bottom: 20px;
	border: 1px solid var(--admin-border);
}

.section-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 12px;
}

.section-header label {
	font-size: 13px;
	color: var(--admin-text-primary);
	font-weight: 500;
	margin: 0;
}

.employer-info {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 10px;
	background-color: var(--admin-hover-bg);
	border-radius: var(--border-radius-s);
	gap: 10px;
}

.employer-name {
	font-size: 14px;
	color: var(--admin-text-primary);
	font-weight: 500;
	flex: 1;
}

.no-employer {
	padding: 10px;
	color: var(--admin-text-secondary);
	font-size: 13px;
	font-style: italic;
}

/* Dates Section */
.dates-section {
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 15px;
	margin-bottom: 20px;
}

.date-item {
	padding: 12px;
	background-color: var(--admin-hover-bg);
	border-radius: var(--border-radius-s);
}

.date-item label {
	display: block;
	font-size: 12px;
	color: var(--admin-text-secondary);
	margin-bottom: 6px;
	font-weight: 500;
}

.date-value {
	font-size: 14px;
	color: var(--admin-text-primary);
}

/* DPA Section */
.dpa-section {
	background-color: rgba(255, 235, 59, 0.1);
	border-left: 3px solid #ffc107;
	padding: 15px;
	border-radius: var(--border-radius-s);
	margin-bottom: 20px;
}

.dpa-warning {
	display: flex;
	gap: 10px;
	align-items: flex-start;
	margin-bottom: 12px;
	font-size: 13px;
	color: var(--admin-text-primary);
	font-weight: 500;
}

.form-checkbox {
	width: 18px;
	height: 18px;
	cursor: default;
	accent-color: var(--background-color-primary);
	flex-shrink: 0;
	margin-top: 2px;
}

.dpa-text {
	margin: 0;
	font-size: 12px;
	color: var(--admin-text-secondary);
}

/* Actions Row */
.actions-row {
	display: flex;
	gap: 10px;
	margin-bottom: 20px;
}

/* Link modal custom override */
.link-modal {
	max-width: 500px;
}

.modal-body {
	padding: 20px;
	color: var(--admin-text-primary);
	font-size: 14px;
	line-height: 1.6;
	max-height: 400px;
	overflow-y: auto;
}

.modal-body .search-box {
	margin-bottom: 15px;
}

.employers-list {
	display: flex;
	flex-direction: column;
	gap: 5px;
}

.employer-option {
	padding: 12px;
	background-color: var(--admin-hover-bg);
	color: var(--admin-text-primary);
	cursor: pointer;
	border-radius: var(--border-radius-s);
	font-size: 13px;
	transition: all 0.2s ease;
	margin-top: 5px;
}

.employer-option:hover {
	background-color: var(--admin-active-bg);
	transform: translateX(4px);
}

.modal-footer {
	padding: 20px;
	border-top: 1px solid var(--admin-border);
	display: flex;
	gap: 10px;
	justify-content: flex-end;
}

/* Responsive */
@media (max-width: 1024px) {
	.customers-layout {
		grid-template-columns: 250px 1fr;
	}
}

@media (max-width: 768px) {
	.customers-layout {
		grid-template-columns: 1fr;
		gap: 15px;
	}

	.customers-list-panel {
		max-height: 400px;
	}

	.form-row {
		grid-template-columns: 1fr;
	}

	.dates-section {
		grid-template-columns: 1fr;
	}

	.customers-page {
		padding: 10px;
	}

	.modal-dialog {
		max-width: 90%;
	}

	.link-modal {
		max-width: 90%;
	}
}

/* CustomersPage-specific overrides */

/* AIOP code display - uses .code-block styles from common.css */
.aiop-code {
	display: block;
	background-color: var(--admin-hover-bg);
	padding: 12px;
	border-radius: var(--border-radius-s);
	border: 1px solid var(--admin-border);
	color: var(--admin-text-primary);
	font-size: 12px;
	max-height: 300px;
	overflow-y: auto;
	font-family: 'Courier New', monospace;
	white-space: pre-wrap;
	word-wrap: break-word;
	line-height: 1.4;
}

.fact-card-wrapper {
	max-height: 400px;
	overflow-y: auto;
	margin-bottom: 15px;
	border: 1px solid var(--admin-border);
	border-radius: var(--border-radius-s);
}

.fact-card {
	background-color: var(--admin-hover-bg);
	padding: 15px;
}

.fact-row {
	display: grid;
	grid-template-columns: 200px 1fr;
	gap: 15px;
	margin-bottom: 12px;
	align-items: flex-start;
}

.fact-row:last-child {
	margin-bottom: 0;
}

.fact-row label {
	font-weight: 500;
	color: var(--admin-text-primary);
	font-size: 13px;
	word-break: break-word;
	word-wrap: break-word;
	padding-top: 10px;
}

/* Credit Modal Styles */
.credit-section {
	margin-bottom: 30px;
	padding-bottom: 20px;
	border-bottom: 1px solid var(--admin-border);
}

.credit-section h4 {
	margin-top: 0;
	margin-bottom: 15px;
	font-size: 14px;
	color: var(--admin-text-primary);
	font-weight: 600;
}

.credit-section .form-group {
	display: flex;
	flex-direction: column;
	gap: 8px;
	margin-bottom: 15px;
}

.credit-section .form-group label {
	font-size: 13px;
	font-weight: 500;
	color: var(--admin-text-primary);
}

.credit-section .form-input {
	padding: 10px;
	border: 1px solid var(--admin-border);
	border-radius: var(--border-radius-s);
	background-color: var(--admin-bg-secondary);
	color: var(--admin-text-primary);
	font-size: 13px;
}

.credit-section .form-input::placeholder {
	color: var(--admin-text-secondary);
}

.credit-history h4 {
	margin-top: 0;
	margin-bottom: 15px;
	font-size: 14px;
	color: var(--admin-text-primary);
	font-weight: 600;
}

/* Label with Button */
.label-with-button {
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 8px;
}

.label-with-button label {
	margin: 0;
}

.label-with-button .action-btn {
	padding: 4px 12px;
	font-size: 12px;
}

/* Customer Note Group */
.customer-note-group {
	margin-bottom: 30px;
}

/* Snackbar positioning - much lower on screen */
:deep(.v-snackbar) {
	bottom: 100px !important;
	top: auto !important;
	margin-top: 0 !important;
}

/* Form Textarea Styles */
.form-textarea {
	width: 100%;
	padding: 10px;
	border: 1px solid var(--admin-border);
	border-radius: var(--border-radius-s);
	background-color: var(--admin-bg-secondary);
	color: var(--admin-text-primary);
	font-size: 13px;
	font-family: inherit;
	resize: vertical;
	transition: all 0.2s ease;
}

.form-textarea:focus {
	outline: none;
	border-color: var(--background-color-primary);
	box-shadow: 0 0 0 2px rgba(51, 40, 71, 0.1);
}

</style>
